import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

import productsLable from "../../assets/product_labels_hero.png";
import barcodeLable from "../../assets/barcode_labels.png";
import pharmaLable from "../../assets/pharma_labels.png";
import foodLable from "../../assets/food_labels.png";
import cosmeticLable from "../../assets/cosmetic_labels.png";

const ProductCard = ({ product, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "-80px",
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      style={{
        transform: "translate3d(0,0,0)",
        willChange: "transform, opacity",
      }}
    >
      {/* STATIC CARD (no animation here) */}
      <div className="group relative bg-[#0d1d33] border border-white/5 rounded-[32px] overflow-hidden transition-all duration-500 hover:border-blue-500/40 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]">
        {/* IMAGE */}
        <div className="relative h-[240px] overflow-hidden">
          <img
            src={product.img}
            alt={`${product.title} - Custom Label Printing Surat India`}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            style={{
              transform: "translate3d(0,0,0)",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1d33] via-transparent to-transparent opacity-60" />

          {product.tag && (
            <div
              className={`absolute top-4 right-4 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${product.tagStyle}`}
            >
              {product.tag}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="p-8">
          <h3 className="text-white font-black text-xl mb-3 tracking-tight group-hover:text-blue-400 transition-colors">
            {product.title}
          </h3>

          <p className="text-white/50 text-sm leading-relaxed mb-6">
            {product.desc}
          </p>

          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-blue-400 font-bold text-xs uppercase tracking-widest hover:text-white transition-all group/btn"
          >
            Request Sample
            <span className="transition-transform duration-300 group-hover/btn:translate-x-1">
              →
            </span>
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const ProductSection = () => {
  const products = [
    {
      title: "Product Labels",
      desc: "Custom-branded product labels for consumer goods. Available in gloss, matte, foil, and embossed finishes.",
      img: productsLable,
      tag: "Bestseller",
      tagStyle: "bg-orange-400 text-orange-900 border border-orange-500/20",
    },
    {
      title: "Barcode & Thermal Labels",
      desc: "High-scan-rate barcode and QR labels for inventory, logistics and retail POS systems. Direct thermal & thermal transfer.",
      img: barcodeLable,
    },
    {
      title: "Pharmaceutical Labels",
      desc: "GMP-compliant labels for medicines, vials, and medical devices. Tamper-evident and heat-resistant options available.",
      img: pharmaLable,
      tag: "Regulated",
      tagStyle: "bg-blue-500 text-white border border-blue-600/20",
    },
    {
      title: "Food Labels",
      desc: "FDA-compliant food-grade labels with vivid colors and water/grease resistance for packaged food products.",
      img: foodLable,
    },
    {
      title: "Cosmetic Labels",
      desc: "Luxury labels with metallic foils, spot UV, and soft-touch lamination — ideal for beauty and personal care brands.",
      img: cosmeticLable,
      tag: "Premium",
      tagStyle:
        "bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 border border-yellow-600/20",
    },
  ];

  return (
    <section id="products" className="py-24 bg-[#0a1628] overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* HEADER */}
        <div className="text-center mb-16">
          <div className="text-blue-400 border border-blue-400/20 font-black text-xs mb-6 px-5 py-2 w-fit mx-auto rounded-full uppercase tracking-[0.2em] bg-blue-500/5">
            Our Products
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
            Labels For Every Industry
          </h2>

          <p className="text-white/40 max-w-[650px] mx-auto text-[16px] sm:text-lg">
            From barcode stickers to luxury cosmetic labels — we craft every
            type of label with premium quality materials.
          </p>
        </div>

        {/* GRID */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, i) => (
            <ProductCard key={i} product={product} index={i} />
          ))}

          {/* CTA CARD */}

          <div className="rounded-[32px] flex items-center justify-center text-center p-8 bg-gradient-to-br from-blue-600 to-blue-700 border border-blue-400/30 relative overflow-hidden group shadow-2xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />

            <div className="relative z-10">
              <div className="text-5xl mb-6 transform group-hover:scale-105 transition-transform duration-500">
                📋
              </div>

              <h3 className="text-white font-black text-2xl mb-4 tracking-tight">
                Need Something Custom?
              </h3>

              <p className="text-white/80 text-sm mb-8 max-w-[280px] mx-auto font-medium">
                We handle unique label specifications, die-cuts, holograms, and
                security labels.
              </p>

              <a
                href="#contact"
                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all shadow-xl shadow-black/10"
              >
                Get Custom Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
